// @ts-nocheck
/*
 * Port of views/dashboard/company/create.js (origin/main-dev, 1742 lines).
 * BYTE-IDENTICAL LOGIC â€” every state hook, useEffect, API call, toast,
 * role gate, licence math (validateInput/stepLicence/getLicenceMax/
 * getDemoCap), demo-company special-case (forcedDemo + inheritance from
 * parent demo, demoExpiryDays), default-user flow (role selection,
 * signup email verify OTP, ACL loading), and JSX condition matches v1
 * to the letter. Chrome kept as-is: react-bootstrap Form/Row/Col/Button,
 * react-select, react-icons, and the ported partial components.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button as _ShadButton } from '@/components/ui/button';

// Inline micro-shim block: replaces `@/lib/rb-shim` Row/Col/Form/Button
// with native bootstrap form-control markup + shadcn Button. JSX intact.
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
import {
  API_COMPANY_CREATE,
  API_PARENT_COMPANY_LICENCE,
  API_MANAGE_LOCATIONS_LIST,
  API_MANAGE_GROUP_LISTING,
  API_CALL_STRATEGIES_MASTER_LIST,
  API_COMPANY_PROFILE,
  API_COUNTRY_CODE_DIRECTIONS_MASTER_LIST,
  API_COMPANY_UPLOAD_LOGO,
  API_USERS_LIST,
  API_TTS_PLANS_LIST,
  API_TTS_PLANS_CREATE,
  API_DEMO_ACCOUNT_SETTINGS_LIST,
} from '@/service/url';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { useUsernameAvailability, useUserEmailAvailability } from '@/hooks/useAvailability';
import {
  TTS_UNIT_RUPEES,
  TTS_UNIT_PAISE,
  TTS_PLAN_NAME_REGEX,
  sanitizeTtsPriceInput,
  toWholeTtsPrice,
  isNewTtsPlanComplete,
  getAssignedTtsPlanFloor,
  getTtsPlanFloorError,
} from '@/utilities/ttsPlanPricing';
import { postApiCall, getApiCall, fetchAndSetFlashMessage } from '@/service/httpCall';
import { useNavigate } from 'react-router-dom';
import {
  ROUTES,
  USER_ROLES,
  MODULE_ACL,
  FLASH_MESSAGE_ACTION_OBJECT,
  BACKEND_API_BASE_URL,
} from '@/utilities/constant';
import { getDefaultUserRolesForCompanyCreate } from '@/utilities/defaultUserRolesCompanyCreate';
import RenderUserCompanyFields from '@/components/partials/dashboard/users/company';
import { validatePhone } from '@/utilities/helpers';
import {
  validateEmail,
  getUsernameValidationError,
  getNameValidationError,
  getUsernameTakenMessage,
  validateUserCoreFields,
  isPasswordValid,
  loadAclOptionsForRole,
  sendSignupEmailCode,
  verifySignupEmailOtp,
  generateRandomPassword,
} from '@/utilities/userCreation';
import { creatorRequiresEmailVerificationOnUserForms } from '@/utilities/userEmailVerificationAcl';
import { getIndiaDirectionIds } from '@/utilities/agentCountryCode';

import Select from 'react-select';
import { SelectAllToggle } from '@/components/ui/select-all-toggle';
import { Storage } from '@/utilities/setting';
import {
  FiPhone,
  FiUsers,
  FiActivity,
  FiShare2,
  FiSmartphone,
  FiGlobe,
  FiUserCheck,
  FiCpu,
} from 'react-icons/fi';
import { FaBuilding, FaHeadset } from 'react-icons/fa';
import LicenceAllocationPanel from '@/components/partials/dashboard/company/LicenceAllocationPanel';
import DefaultUserForm from '@/components/partials/dashboard/company/DefaultUserForm';
import { toDateInputString, todayInputString, minDateStr } from '@/utilities/expiryDate';
import { blockManualDateEntry, blockDatePaste } from '@/utilities/dateInputHelpers';
import { Card as ShadcnCard } from '@/components/ui/card';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as ShadPageButton } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Building2, Sliders, Award, Image, UserPlus, Check } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES as PAGE_ROUTES } from '@/utilities/constant';
import { Stepper, useStepper, type StepDescriptor } from '@/components/ui/stepper';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import CompanyBrandingFields, {
  handleCompanyLogoChange,
} from './components/CompanyBrandingFields';
import {
  POSTPAID_CREDIT_MAX,
  billingPlanFormError,
  formatCreditLimit,
  postpaidCreditCeiling,
} from '@/utilities/billingPlan';
import '@/features/users/user-forms.css';

// Ordered step descriptors for the 4-step wizard. Order and IDs match the
// section boundaries inside the form JSX below — see the `stepper.current === N`
// conditionals wrapping each section.
const CREATE_COMPANY_STEPS: StepDescriptor[] = [
  { id: 'details', label: 'Details', description: 'Name, address, expiry', icon: Building2 },
  { id: 'setup', label: 'Setup', description: 'Strategy, TTS, type', icon: Sliders },
  { id: 'licence', label: 'Licence', description: 'Quotas from parent', icon: Award },
  { id: 'branding', label: 'Branding', description: 'Domain, panel name, logo', icon: Image },
  { id: 'user', label: 'Default User', description: 'Optional admin', icon: UserPlus },
];

const CompanyCreate = () => {
  const navigate = useNavigate();

  // Multi-step wizard. Every original state / validation / submit path is
  // untouched — the stepper only gates which fields are visible right
  // now. `stepper.goTo` lets the user click a completed step in the
  // header; `stepper.next()` marks the current step complete.
  const stepper = useStepper(CREATE_COMPANY_STEPS.length);
  // When a step's validation fails, flip this so the child company-fields
  // component surfaces every "required" inline message even for fields
  // the user hasn't touched yet.
  const [forceShowStep0Errors, setForceShowStep0Errors] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPincode, setCompanyPincode] = useState('');
  const [companyExpiryDate, setCompanyExpiryDate] = useState('');
  const [companyLicence, setCompanyLicence] = useState('');
  const [parentLicence, setParentLicence] = useState(null);
  const [callCenterAdminTeamlead, setCallCenterAdminTeamlead] = useState(0);
  const [agent, setAgent] = useState(0);
  const [liveAgent, setLiveAgent] = useState(0);
  const [demoLiveAgents, setDemoLiveAgents] = useState(0);
  const [teamLead, setTeamLead] = useState(0);
  const [channel, setChannel] = useState(0);
  const [aiAgent, setAiAgent] = useState(0);
  const [aiAgentLiveChannel, setAiAgentLiveChannel] = useState(0);
  const [errors, setErrors] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [mobile, setMobile] = useState(0);
  const [availableLicence, setAvailableLicence] = useState({});
  const [asReseller, setAsReseller] = useState(0);
  const [asCompany, setAsCompany] = useState(0);
  const [loginUserRole, setLoginUserRole] = useState(null);
  const [locations, setLocations] = useState([]);
  const [callStrategies, setCallStrategies] = useState([]);
  const [selectedCallStrategy, setSelectedCallStrategy] = useState([]);
  const [countryCodeDirections, setCountryCodeDirections] = useState([]);
  const [selectedCountryCodeDirections, setSelectedCountryCodeDirections] = useState([]);
  const indiaDirectionsDefaultedRef = useRef(false);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [isResellerCompany, setIsResellerCompany] = useState(null);
  const [isOwnCompany, setIsOwnCompany] = useState(null);
  const [billingType, setBillingType] = useState('');
  const [postpaidMode, setPostpaidMode] = useState('');
  const [creditLimit, setCreditLimit] = useState('');
  const [limitPeriod, setLimitPeriod] = useState('');
  const [dailyLimit, setDailyLimit] = useState('');
  const [weeklyLimit, setWeeklyLimit] = useState('');
  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [parentIsPrepaid, setParentIsPrepaid] = useState(false);
  const [parentPostpaidCredit, setParentPostpaidCredit] = useState<number | null>(null);
  const [parentLimitPeriod, setParentLimitPeriod] = useState<'' | 'daily' | 'weekly' | 'monthly'>('');
  const [resellerCreationBlocked, setResellerCreationBlocked] = useState(false);
  const [notes, setNotes] = useState([]);
  const [cps, setCps] = useState(0);

  const [defaultUserName, setDefaultUserName] = useState('');
  const [defaultUsername, setDefaultUsername] = useState('');
  const [defaultUsernameError, setDefaultUsernameError] = useState('');
  const [defaultNameError, setDefaultNameError] = useState('');
  const [defaultUsernameTakenMessage, setDefaultUsernameTakenMessage] = useState('');
  const [existingUsernames, setExistingUsernames] = useState([]);
  const [defaultUserEmail, setDefaultUserEmail] = useState('');
  const [initialPassword] = useState(generateRandomPassword);
  const [defaultUserPassword, setDefaultUserPassword] = useState(initialPassword);
  const [defaultUserConfirmPassword, setDefaultUserConfirmPassword] = useState(initialPassword);
  const [defaultUserExpiryDate, setDefaultUserExpiryDate] = useState('');
  const [defaultUserAclList, setDefaultUserAclList] = useState([]);
  const [defaultUserAcl, setDefaultUserAcl] = useState('');
  const [parentExpiryDate, setParentExpiryDate] = useState(null);
  const [showDefaultPassword, setShowDefaultPassword] = useState(true);
  const [signupEmailOtp, setSignupEmailOtp] = useState('');
  const [signupEmailToken, setSignupEmailToken] = useState('');
  const [emailVerifiedForCreate, setEmailVerifiedForCreate] = useState(false);
  const [signupEmailBusy, setSignupEmailBusy] = useState(false);
  const [defaultUserRoleSelection, setDefaultUserRoleSelection] = useState('');
  const [defaultUserAccountType, setDefaultUserAccountType] = useState('0');
  const [isCreatorSuperAdmin, setIsCreatorSuperAdmin] = useState(false);
  const [isCreatorCompanyAdmin, setIsCreatorCompanyAdmin] = useState(false);
  const [creatorAssignedIsOwnCompany, setCreatorAssignedIsOwnCompany] = useState(false);
  const [isCreatorCallCenterAdmin, setIsCreatorCallCenterAdmin] = useState(false);
  const [isCreatorReseller, setIsCreatorReseller] = useState(false);
  const [resellerChainParentRole, setResellerChainParentRole] = useState('');
  const [resellerChainSuperParentRole, setResellerChainSuperParentRole] = useState('');
  const [resellerChainSuperSuperParentRole, setResellerChainSuperSuperParentRole] = useState('');
  const [domain, setDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [panelDisplayName, setPanelDisplayName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoPath, setLogoPath] = useState('');
  const [logoError, setLogoError] = useState('');
  const [applyTtsPlan, setApplyTtsPlan] = useState(false);
  const [tts_plan_id, setTtsPlanId] = useState('');
  const [ttsPlanList, setTtsPlanList] = useState([]);
  const ttsPlansAcl = useModuleAcl(MODULE_ACL.TTS_PLANS);
  const [ttsPlanMode, setTtsPlanMode] = useState('existing');
  const [newTtsPlanName, setNewTtsPlanName] = useState('');
  const [newTtsUnitBalance, setNewTtsUnitBalance] = useState(TTS_UNIT_RUPEES);
  const [newTtsPerCharacter, setNewTtsPerCharacter] = useState('');
  const [newTtsPerVariable, setNewTtsPerVariable] = useState('');
  const newTtsAllowDecimals = newTtsUnitBalance === TTS_UNIT_PAISE;
  const ttsPlanFloor = useMemo(() => {
    try {
      return getAssignedTtsPlanFloor(JSON.parse(Storage.getItem('user') || 'null'));
    } catch {
      return null;
    }
  }, []);
  const newTtsFloorError = getTtsPlanFloorError(ttsPlanFloor, {
    per_character: newTtsPerCharacter,
    per_variable: newTtsPerVariable,
    unit_balance: newTtsUnitBalance,
  });
  const newTtsPlanReady =
    isNewTtsPlanComplete(
      newTtsPlanName,
      newTtsUnitBalance,
      newTtsPerCharacter,
      newTtsPerVariable,
    ) && !newTtsFloorError;
  const ttsPlanIncomplete =
    applyTtsPlan && (ttsPlanMode === 'new' ? !newTtsPlanReady : !tts_plan_id);
  const ttsPlanErrorMessage =
    ttsPlanMode === 'new'
      ? newTtsFloorError || 'Please fill in the new TTS plan details'
      : 'Please select a TTS plan';
  const [createDefaultUser, setCreateDefaultUser] = useState(true);

 
  const defaultUsernameStatus = useUsernameAvailability(defaultUsername, {
    enabled: createDefaultUser && !getUsernameValidationError(defaultUsername),
  });
  const defaultEmailStatus = useUserEmailAvailability(defaultUserEmail, {
    enabled: createDefaultUser && !validateEmail(defaultUserEmail),
  });
  const [isDemoCompany, setIsDemoCompany] = useState(false);
  const [demoSettings, setDemoSettings] = useState(null);
  const [forcedDemo, setForcedDemo] = useState(false);

  useEffect(() => {
    getApiCall(API_DEMO_ACCOUNT_SETTINGS_LIST, {})
      .then((resp) => {
        if (Array.isArray(resp) && resp.length > 0) setDemoSettings(resp[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const u = JSON.parse(Storage.getItem('user') || 'null');
      const company =
        u?.assignedCompany || u?.assigned_company_details || u?.company || null;
      if (company?.is_demo_company === true) {
        setForcedDemo(true);
        setIsDemoCompany(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    setDefaultUserExpiryDate((prev) => {
      if (!companyExpiryDate) return prev || '';
      if (!prev) return companyExpiryDate;
      return prev > companyExpiryDate ? companyExpiryDate : prev;
    });
  }, [companyExpiryDate]);

  const defaultUserMaxExpiry = minDateStr(companyExpiryDate, parentExpiryDate);

  const demoExpiryDays = Number(demoSettings?.expiry_days_per_recharge) || 0;
  useEffect(() => {
    if (!isDemoCompany) return;
    if (forcedDemo && parentExpiryDate) {
      const p = new Date(parentExpiryDate);
      if (!Number.isNaN(p.getTime())) {
        const yyyy = p.getFullYear();
        const mm = String(p.getMonth() + 1).padStart(2, '0');
        const dd = String(p.getDate()).padStart(2, '0');
        setCompanyExpiryDate(`${yyyy}-${mm}-${dd}`);
        return;
      }
    }
    if (demoExpiryDays <= 0) return;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + (demoExpiryDays - 1));
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    setCompanyExpiryDate(`${yyyy}-${mm}-${dd}`);
  }, [isDemoCompany, demoExpiryDays, forcedDemo, parentExpiryDate]);

  useEffect(() => {
    if (!isDemoCompany) return;
    setBillingType('prepaid');
    setPostpaidMode('');
  }, [isDemoCompany]);

  const demoLicenceLocked = isDemoCompany && !!demoSettings;

  useEffect(() => {
    if (!demoLicenceLocked) return;
    const perLive = Number(demoSettings.total_demo_live_agent_per_company) || 0;
    setCallCenterAdminTeamlead(String(Number(demoSettings.call_center_per_company) || 0));
    setAgent(String(Number(demoSettings.total_demo_agent_per_company) || 0));
    setTeamLead(String(Number(demoSettings.team_lead_per_company) || 0));
    setLiveAgent(String(perLive));
    setChannel(String(perLive * 2));
  }, [demoLicenceLocked, demoSettings]);

  const defaultUserEmailVerificationRequired = useMemo(() => {
    try {
      const creatorUser = JSON.parse(Storage.getItem('user') || 'null');
      return creatorRequiresEmailVerificationOnUserForms(creatorUser, {
        isSuperAdmin: isCreatorSuperAdmin,
        userRole: creatorUser?.role,
      });
    } catch {
      return false;
    }
  }, [isCreatorSuperAdmin]);

  useEffect(() => {
    if (!defaultUserEmailVerificationRequired) {
      setSignupEmailToken('');
      setSignupEmailOtp('');
      setEmailVerifiedForCreate(false);
    }
  }, [defaultUserEmailVerificationRequired]);

  const handleResellerChange = (checked) => {
    if (resellerCreationBlocked) {
      setFormErrors((prev) => ({
        ...prev,
        companyType: 'Reseller chain limit reached. You can only create an End User.',
      }));
      return;
    }
    setIsResellerCompany(checked);
    setIsOwnCompany(false);
    if (checked) setIsDemoCompany(false);
    setFormErrors((prev) => ({ ...prev, companyType: '' }));
  };

  const handleOwnCompanyChange = (checked) => {
    setIsOwnCompany(checked);
    setIsResellerCompany(false);
    setFormErrors((prev) => ({ ...prev, companyType: '' }));
  };

  const handleDemoCompanyChange = (checked) => {
    if (forcedDemo) return;
    setIsDemoCompany(checked);
    if (checked) {
      setIsOwnCompany(true);
      setIsResellerCompany(false);
    }
  };

  useEffect(() => {
    const fetchMetaData = async () => {
      const user = JSON.parse(Storage.getItem('user'));

      try {
        const [locationsData, groupsData, callStrategiesData, countryCodeDirectionsData] =
          await Promise.all([
            getApiCall(API_MANAGE_LOCATIONS_LIST, {}),
            getApiCall(API_MANAGE_GROUP_LISTING, {}),
            getApiCall(API_CALL_STRATEGIES_MASTER_LIST, {}),
            getApiCall(API_COUNTRY_CODE_DIRECTIONS_MASTER_LIST, {}).catch(() => []),
          ]);

        setCallStrategies(
          callStrategiesData?.map((strategy) => ({
            value: strategy.id,
            label: strategy.name,
          })) || [],
        );

        setCountryCodeDirections(
          Array.isArray(countryCodeDirectionsData) ? countryCodeDirectionsData : [],
        );

        if (user.role === USER_ROLES.SUPERADMIN) {
          const normalizedLocations =
            (locationsData || []).map((loc) => ({
              value: loc.location_id,
              label: loc.location_name,
            })) || [];
          const normalizedGroups =
            (groupsData || []).map((g) => ({ value: g.group_id, label: g.group_name })) || [];
          setLocations(normalizedLocations);
          setGroups(normalizedGroups);
        } else {
          const userGroups =
            groupsData.map((ug) => ({ value: ug.group_id, label: ug.group_name })) || [];
          const userLocations =
            locationsData?.map((ul) => ({
              value: ul.location_id,
              label: ul.location_name,
            })) || [];
          setGroups(userGroups);
          setLocations(userLocations);
        }
      } catch (error) {
        console.error('Error fetching Dialer groups/locations:', error);
      }
    };
    fetchMetaData();

    fetchAndSetFlashMessage(
      (msg) =>
        msg.module_name === MODULE_ACL.COMPANY &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD &&
        msg.type === 'Note',
      setNotes,
    );
  }, []);

  useEffect(() => {
    if (indiaDirectionsDefaultedRef.current) return;
    if (!countryCodeDirections.length) return;
    indiaDirectionsDefaultedRef.current = true;
    if (selectedCountryCodeDirections.length > 0) return;
    const ids = getIndiaDirectionIds(countryCodeDirections);
    if (ids.length) setSelectedCountryCodeDirections(ids);
  }, [countryCodeDirections, selectedCountryCodeDirections.length]);

  useEffect(() => {
    getUser();
  }, []);

  const defaultUserRoleSelectOptions = useMemo(
    () =>
      getDefaultUserRolesForCompanyCreate({
        isSuperAdmin: isCreatorSuperAdmin,
        isCompanyAdmin: isCreatorCompanyAdmin,
        creatorAssignedIsOwnCompany,
        isCallCenterAdmin: isCreatorCallCenterAdmin,
        isReseller: isCreatorReseller,
        loggedUserParent: resellerChainParentRole,
        loggedUserSuperParent: resellerChainSuperParentRole,
        loggedUserSuperSuperParent: resellerChainSuperSuperParentRole,
        isCreatingResellerCompany: Boolean(isResellerCompany),
        isCreatingEndUserCompany: Boolean(isOwnCompany),
      }),
    [
      isCreatorSuperAdmin,
      isCreatorCompanyAdmin,
      creatorAssignedIsOwnCompany,
      isCreatorCallCenterAdmin,
      isCreatorReseller,
      resellerChainParentRole,
      resellerChainSuperParentRole,
      resellerChainSuperSuperParentRole,
      isResellerCompany,
      isOwnCompany,
    ],
  );

  useEffect(() => {
    const allowed = defaultUserRoleSelectOptions.map((o) => o.value);
    if (!allowed.length) {
      setDefaultUserRoleSelection('');
      return;
    }
    setDefaultUserRoleSelection((prev) => {
      if (prev && allowed.includes(prev)) return prev;
      if (isOwnCompany && allowed.includes(USER_ROLES.COMPANY_ADMIN)) {
        return USER_ROLES.COMPANY_ADMIN;
      }
      if (isCreatorSuperAdmin && allowed.includes(USER_ROLES.RESELLER)) {
        return USER_ROLES.RESELLER;
      }
      if (isResellerCompany && allowed.includes(USER_ROLES.RESELLER)) {
        return USER_ROLES.RESELLER;
      }
      return allowed[0];
    });
  }, [defaultUserRoleSelectOptions, isResellerCompany, isOwnCompany, isCreatorSuperAdmin]);

  useEffect(() => {
    if (!defaultUserRoleSelection) {
      setDefaultUserAclList([]);
      setDefaultUserAcl('');
      return;
    }
    loadAclOptionsForRole(defaultUserRoleSelection)
      .then(({ list, defaultAclId }) => {
        setDefaultUserAclList(list);
        setDefaultUserAcl(defaultAclId);
      })
      .catch((err) => console.error(err));
  }, [defaultUserRoleSelection]);

  const canSubmitForm = useMemo(() => {
    if (!(isResellerCompany || isOwnCompany)) return false;
    if (billingPlanFormError(billingType, postpaidMode, parentIsPrepaid, parentPostpaidCredit, limitPeriod, limitPeriod === 'daily' ? dailyLimit : limitPeriod === 'weekly' ? weeklyLimit : monthlyLimit, parentLimitPeriod || null)) return false;
    if (Object.values(errors).some((error) => error !== '')) return false;
    if (resellerCreationBlocked && isResellerCompany) return false;
    if (liveAgent > agent) return false;
    if (isDemoCompany && Number(channel) > Number(liveAgent) * 2) return false;
    if (isDemoCompany && Number(teamLead) > Number(agent)) return false;
    if (isDemoCompany && demoSettings) {
      if (Number(agent) > Number(demoSettings.total_demo_agent_per_company)) return false;
      if (Number(liveAgent) > Number(demoSettings.total_demo_live_agent_per_company)) return false;
    }

    if (!companyName.trim()) return false;
    if (!companyPhone.trim() || !/^[0-9]{10}$/.test(companyPhone.trim())) return false;
    if (!companyPincode.trim() || !/^\d{6}$/.test(companyPincode)) return false;
    if (!companyAddress.trim()) return false;
    if (!companyExpiryDate) return false;
    if (ttsPlanIncomplete) return false;

    if (createDefaultUser) {
      if (getNameValidationError(defaultUserName)) return false;
      if (getUsernameValidationError(defaultUsername)) return false;
      if (getUsernameTakenMessage(defaultUsername, existingUsernames)) return false;
      if (defaultUsernameStatus === 'taken' || defaultEmailStatus === 'taken') return false;
      if (!defaultUserEmail.trim() || validateEmail(defaultUserEmail)) return false;
      if (!defaultUserPassword.trim() || !isPasswordValid(defaultUserPassword)) return false;
      if (
        !defaultUserConfirmPassword.trim() ||
        defaultUserPassword !== defaultUserConfirmPassword
      ) {
        return false;
      }
      if (!defaultUserExpiryDate) return false;
      if (defaultUserExpiryDate < todayInputString()) return false;
      if (defaultUserMaxExpiry && !isDemoCompany) {
        if (defaultUserExpiryDate > defaultUserMaxExpiry) return false;
      }
      if (!defaultUserRoleSelection || !defaultUserAcl) return false;
      if (
        defaultUserEmailVerificationRequired &&
        (!emailVerifiedForCreate || !signupEmailToken)
      ) {
        return false;
      }
      if (isCreatorSuperAdmin) {
        if (
          defaultUserAccountType === '' ||
          defaultUserAccountType === null ||
          defaultUserAccountType === undefined
        ) {
          return false;
        }
        if (![0, 1].includes(Number(defaultUserAccountType))) return false;
      }
    }

    return true;
  }, [
    isResellerCompany,
    isOwnCompany,
    billingType,
    postpaidMode,
    creditLimit,
    dailyLimit,
    weeklyLimit,
    monthlyLimit,
    parentIsPrepaid,
    parentPostpaidCredit,
    parentLimitPeriod,
    limitPeriod,
    errors,
    resellerCreationBlocked,
    liveAgent,
    agent,
    channel,
    isDemoCompany,
    demoSettings,
    teamLead,
    companyName,
    companyPhone,
    companyPincode,
    companyAddress,
    companyExpiryDate,
    ttsPlanIncomplete,
    createDefaultUser,
    defaultUserName,
    defaultUsername,
    existingUsernames,
    defaultUserEmail,
    defaultUserPassword,
    defaultUserConfirmPassword,
    defaultUserExpiryDate,
    parentExpiryDate,
    defaultUserRoleSelection,
    defaultUserAcl,
    defaultUserEmailVerificationRequired,
    emailVerifiedForCreate,
    signupEmailToken,
    isCreatorSuperAdmin,
    defaultUserAccountType,
  ]);

  useEffect(() => {
    getApiCall(API_PARENT_COMPANY_LICENCE)
      .then((resp) => {
        setParentLicence(resp?.parentLicence);
      })
      .catch((error) => {
        toast.error(friendlyApiError(error) || 'Failed to load parent company licence');
      });
  }, []);

  useEffect(() => {
    getApiCall(`${API_USERS_LIST}?role=noRole`, {})
      .then((data) => {
        setExistingUsernames((data || []).map((user) => user.username));
      })
      .catch((error) => console.error('Failed to load usernames', error));
  }, []);

  useEffect(() => {
    getApiCall(API_TTS_PLANS_LIST)
      .then((resp) => setTtsPlanList(resp || []))
      .catch((error) => console.error('Failed to load TTS plans', error));
  }, []);

  const validateInput = (value, key) => {
    if (value === '' || value == null) return 0;
    const digitsOnly = String(value).replace(/\D/g, '');
    if (digitsOnly === '') return 0;
    const num = parseInt(digitsOnly, 10);
    let newErrors = { ...errors };

    if (Number.isNaN(num)) return 0;
    if (num < 0) {
      newErrors[key] = 'Value cannot be negative';
      setErrors(newErrors);
      return 0;
    }

    const licenceKeyMap = {
      role_ccadmin: 'role_ccadmin',
      agent: 'role_ccagent',
      live_agent: 'live_agent',
      team_lead: 'role_teamlead',
      channel: 'channel',
      cps: 'cps',
      mobile: 'mobile',
      reseller: 'reseller',
      company: 'company',
      demo_live_agents: 'demo_live_agents_per_reseller_company',
      ai_agent: 'ai_agent',
      ai_agent_live_channel: 'ai_agent_live_channel',
    };

    const licenceKey = licenceKeyMap[key];
    const availableValue = parentLicence?.available_licence?.[licenceKey];

    if (availableValue === 0 && num > 0) {
      newErrors[key] = 'Value cannot be more than 0';
      setErrors(newErrors);
      return num;
    }

    newErrors[key] = '';

    const nextAgent = key === 'agent' ? num : agent;
    const nextLiveAgent = key === 'live_agent' ? num : liveAgent;
    const nextTeamLead = key === 'team_lead' ? num : teamLead;

    if (nextAgent < nextLiveAgent) {
      newErrors.agent = 'Total Agent cannot be less than Live Agent';
      newErrors.live_agent = 'Live Agent cannot be more than Total Agent';
    } else {
      newErrors.agent = '';
      newErrors.live_agent = '';
    }

    if (nextTeamLead > nextAgent) {
      newErrors.team_lead = `Total Team Lead cannot be more than Total Agent (${nextAgent})`;
    } else {
      newErrors.team_lead = '';
    }

    if (availableValue !== null && availableValue !== undefined && availableValue > 0) {
      if (key === 'company' && num > availableValue - 1) {
        newErrors.company = `Value cannot be more than ${availableValue - 1}`;
      }
      if (key !== 'company' && num > availableValue) {
        newErrors[key] = `Value cannot be more than ${availableValue}`;
      }
    }

    if (key === 'live_agent' && num > nextAgent) {
      newErrors.live_agent = `Live Agent cannot be more than Total Agent (${nextAgent})`;
    }
    if (key === 'team_lead' && num > nextAgent) {
      newErrors.team_lead = `Total Team Lead cannot be more than Total Agent (${nextAgent})`;
    }

    if (isDemoCompany) {
      const nextDemoLive = key === 'demo_live_agents' ? num : Number(demoLiveAgents) || 0;
      if (nextAgent > nextDemoLive) {
        newErrors.demo_live_agents = `Demo company: Total Agent cannot exceed Demo Live Agents (Reseller) (${nextDemoLive})`;
      } else if (String(newErrors.demo_live_agents || '').startsWith('Demo company: Total Agent')) {
        newErrors.demo_live_agents = '';
      }
    }

    let capped = num;
    const demoCap = getDemoCap(key);
    if (demoCap != null && num > demoCap) {
      capped = demoCap;
      newErrors[key] = '';
    }

    setErrors(newErrors);
    return capped;
  };

  const getDemoCap = (key) => {
    if (!isDemoCompany || !demoSettings) return null;
    if (key === 'agent') return Number(demoSettings.total_demo_agent_per_company);
    if (key === 'live_agent') return Number(demoSettings.total_demo_live_agent_per_company);
    if (key === 'channel') return Number(liveAgent) * 2;
    if (key === 'role_ccadmin') return Number(demoSettings.call_center_per_company);
    if (key === 'team_lead') return Number(demoSettings.team_lead_per_company);
    return null;
  };

  const getLicenceMax = (key) => {
    const avail = parentLicence?.available_licence;
    if (avail == null) {
      const demoCapOnly = getDemoCap(key);
      return demoCapOnly != null ? demoCapOnly : undefined;
    }
    const map = {
      role_ccadmin: avail.role_ccadmin,
      agent: avail.role_ccagent,
      live_agent: avail.live_agent,
      team_lead: avail.role_teamlead,
      channel: avail.channel,
      cps: avail.cps,
      mobile: avail.mobile,
      company: avail.company,
      demo_live_agents: avail.demo_live_agents_per_reseller_company,
      ai_agent: avail.ai_agent,
      ai_agent_live_channel: avail.ai_agent_live_channel,
    };
    let max = map[key];
    if (key === 'company' && max != null && max > 0) max = max - 1;
    const demoCap = getDemoCap(key);
    if (demoCap != null) {
      max = max == null ? demoCap : Math.min(max, demoCap);
    }
    return max;
  };

  const stepLicence = (key, current, setter, delta) => {
    const next = Math.max(0, current + delta);
    setter(validateInput(String(next), key));
  };

  const canIncrementLicence = (key, current) => {
    const max = getLicenceMax(key);
    if (max === undefined || max === null) return true;
    return current < max;
  };

  async function getUser() {
    try {
      const user = JSON.parse(Storage.getItem('user'));
      if (user) {
        setLoginUserRole(user.role);

        const isSa = user.role === USER_ROLES.SUPERADMIN;
        const isCa = user.role === USER_ROLES.COMPANY_ADMIN;
        const assignedOwn = Boolean(user?.assignedCompany?.is_own_company);
        const isCc = user.role === USER_ROLES.CALL_CENTRE_ADMIN;
        const isR = user.role === USER_ROLES.RESELLER;

        setIsCreatorSuperAdmin(isSa);
        setIsCreatorCompanyAdmin(isCa);
        setCreatorAssignedIsOwnCompany(assignedOwn);
        setIsCreatorCallCenterAdmin(isCc);
        setIsCreatorReseller(isR);

        if (isR || isCa) {
          setResellerChainParentRole(user.role || '');
          setResellerChainSuperParentRole(user?.createdByUser?.role ?? '');
          setResellerChainSuperSuperParentRole(
            user?.createdByUser?.createdByUser?.role ?? '',
          );
        } else {
          setResellerChainParentRole('');
          setResellerChainSuperParentRole('');
          setResellerChainSuperSuperParentRole('');
        }

        if (user.expiry_date) {
          setParentExpiryDate(user.expiry_date);
        }

        if (!isSa) {
          const assignedId = user?.assignedCompany?.id ?? user?.assigned_company;
          let parentBillingType = user?.assignedCompany?.billing_type || null;
          let parentMode = user?.assignedCompany?.postpaid_mode || null;
          let parentCycle = user?.assignedCompany?.billing_cycle || '';
          let parentCreditRaw = user?.assignedCompany?.credit_limit ?? user?.assignedCompany?.credits_available;
          if (assignedId) {
            try {
              const mine = await getApiCall(API_COMPANY_PROFILE.replace(':id', String(assignedId)));
              parentBillingType = mine?.billing_type || parentBillingType;
              parentMode = mine?.postpaid_mode || parentMode;
              parentCycle = mine?.billing_cycle || parentCycle;
              parentCreditRaw = mine?.credit_limit ?? mine?.credits_available ?? parentCreditRaw;
            } catch (billingErr) {
              console.log(billingErr);
            }
          }
          if (parentBillingType === 'postpaid') {
            const amount = Number(parentCreditRaw);
            const cycle = parentMode === 'limited' && ['daily', 'weekly', 'monthly'].includes(parentCycle)
              ? parentCycle
              : '';
            setParentLimitPeriod(cycle as '' | 'daily' | 'weekly' | 'monthly');
            setParentPostpaidCredit(Number.isFinite(amount) && amount > 0 ? Math.floor(amount) : 0);
          }
          if (parentBillingType === 'prepaid') {
            setParentIsPrepaid(true);
            setBillingType('prepaid');
            setPostpaidMode('');
            setCreditLimit('');
          }
        }
        const myCompany = user.assignedCompany;
        const parentUser = user.createdByUser;
        let parentCompany = null;
        if (parentUser) {
          if (parentUser.assigned_company) {
            const urlToFetch = API_COMPANY_PROFILE.replace(':id', parentUser.assigned_company);
            parentCompany = await getApiCall(urlToFetch);
          }
        }

        const parentRole = user.role;
        const superParentRole = user?.createdByUser?.role ?? '';
        const superSuperParentRole = user?.createdByUser?.createdByUser?.role ?? '';

        const resellerChainFull =
          parentRole === USER_ROLES.RESELLER &&
          superParentRole === USER_ROLES.RESELLER &&
          superSuperParentRole === USER_ROLES.RESELLER;

        if (resellerChainFull) {
          setResellerCreationBlocked(true);
          setIsResellerCompany(false);
          if (user.role !== USER_ROLES.SUPERADMIN) {
            setIsOwnCompany(true);
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  }

  function validateCompanyFields() {
    let valid = true;
    const newFormErrors = {};

    if (!companyName.trim()) {
      valid = false;
    } else if (!companyPhone.trim()) {
      valid = false;
    } else if (!validateCompanyPhone(companyPhone)) {
      valid = false;
    } else if (!companyPincode.trim()) {
      valid = false;
    } else if (!/^\d{6}$/.test(companyPincode)) {
      valid = false;
    } else if (!companyAddress.trim()) {
      valid = false;
      newFormErrors.companyAddress = 'Company address is required';
    } else if (!companyExpiryDate) {
      valid = false;
      newFormErrors.companyExpiry = 'Company expiry date is required';
    } else if (parentExpiryDate && !isDemoCompany) {
      const c = new Date(companyExpiryDate);
      const p = new Date(parentExpiryDate);
      c.setHours(0, 0, 0, 0);
      p.setHours(0, 0, 0, 0);
      if (c.getTime() > p.getTime()) {
        valid = false;
        newFormErrors.companyExpiry = "Company expiry cannot exceed parent company's expiry date";
      }
    }
    if (!isResellerCompany && !isOwnCompany) {
      newFormErrors.companyType = 'Please select either Reseller Company or End User';
      setFormErrors((prev) => ({ ...prev, ...newFormErrors }));
      return false;
    }
    const billingError = billingPlanFormError(
      billingType,
      postpaidMode,
      parentIsPrepaid,
      parentPostpaidCredit,
      limitPeriod,
      limitPeriod === 'daily' ? dailyLimit : limitPeriod === 'weekly' ? weeklyLimit : monthlyLimit,
      parentLimitPeriod || null,
    );
    if (billingError) {
      newFormErrors.billingPlan = billingError;
      valid = false;
    }
    if (ttsPlanIncomplete) {
      valid = false;
      newFormErrors.ttsPlan = ttsPlanErrorMessage;
    }

    const newLicenceErrors = {};
    if (isDemoCompany && Number(channel) > Number(liveAgent) * 2) {
      valid = false;
      newLicenceErrors.channel = 'Channels cannot exceed Total Live Agent * 2';
    }
    if (isDemoCompany && Number(teamLead) > Number(agent)) {
      valid = false;
      newLicenceErrors.team_lead =
        'Total Team Lead is inclusive in Total Agent and cannot exceed it';
    }
    if (isDemoCompany && demoSettings) {
      if (Number(agent) > Number(demoSettings.total_demo_agent_per_company)) {
        valid = false;
        newLicenceErrors.agent = `Total Agent cannot exceed ${demoSettings.total_demo_agent_per_company}`;
      } else if (Number(liveAgent) > Number(demoSettings.total_demo_live_agent_per_company)) {
        valid = false;
        newLicenceErrors.live_agent = `Total Live Agent cannot exceed ${demoSettings.total_demo_live_agent_per_company}`;
      }
    }
    if (Object.keys(newLicenceErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newLicenceErrors }));
    }

    setFormErrors((prev) => ({ ...prev, ...newFormErrors }));
    return valid;
  }

  function validateDefaultUserFields() {
    let valid = true;
    const newFormErrors = {};

    const core = validateUserCoreFields({
      name: defaultUserName,
      username: defaultUsername,
      email: defaultUserEmail,
      password: defaultUserPassword,
      confirmPassword: defaultUserConfirmPassword,
      existingUsernames,
      role: defaultUserRoleSelection,
      aclId: defaultUserAcl,
      emailVerificationRequired: defaultUserEmailVerificationRequired,
      emailVerified: emailVerifiedForCreate,
      signupEmailToken,
      labelPrefix: 'Default user ',
      usernameStatus: defaultUsernameStatus,
      emailStatus: defaultEmailStatus,
    });

    if (!core.valid) valid = false;
    if (core.nameError) setDefaultNameError(core.nameError);
    if (core.usernameError) setDefaultUsernameError(core.usernameError);
    if (core.usernameTakenMessage) setDefaultUsernameTakenMessage(core.usernameTakenMessage);
    if (core.emailError) newFormErrors.defaultUserEmail = core.emailError;
    else if (core.emailTakenMessage) newFormErrors.defaultUserEmail = core.emailTakenMessage;
    if (core.passwordError) newFormErrors.defaultUserPassword = core.passwordError;
    if (core.confirmPasswordError)
      newFormErrors.defaultUserConfirmPassword = core.confirmPasswordError;
    if (core.roleError) newFormErrors.defaultUserRole = core.roleError;
    if (core.aclError) newFormErrors.defaultUserAcl = core.aclError;
    if (core.emailVerifyError) newFormErrors.defaultUserEmailVerify = core.emailVerifyError;

    if (!defaultUserExpiryDate) {
      valid = false;
      newFormErrors.defaultUserExpiry = 'Default user expiry date is required';
    } else if (defaultUserExpiryDate < todayInputString()) {
      valid = false;
      newFormErrors.defaultUserExpiry = 'Default user expiry date cannot be in the past';
    } else if (defaultUserMaxExpiry && !isDemoCompany) {
      if (defaultUserExpiryDate > defaultUserMaxExpiry) {
        valid = false;
        newFormErrors.defaultUserExpiry = `Default user expiry cannot be later than ${defaultUserMaxExpiry} (the company's expiry)`;
      }
    }
    if (isCreatorSuperAdmin) {
      if (
        defaultUserAccountType === '' ||
        defaultUserAccountType === null ||
        defaultUserAccountType === undefined
      ) {
        valid = false;
        newFormErrors.defaultUserAccountType = 'Default user account type is required';
      } else if (![0, 1].includes(Number(defaultUserAccountType))) {
        valid = false;
        newFormErrors.defaultUserAccountType = 'Account Type must be Promotional or Transactional';
      }
    }
    setFormErrors((prev) => ({ ...prev, ...newFormErrors }));
    return valid;
  }

  const handleDefaultUsernameChange = (e) => {
    const value = e.target.value;
    setDefaultUsername(value);
    const err = getUsernameValidationError(value);
    setDefaultUsernameError(err);
    const taken = getUsernameTakenMessage(value, existingUsernames);
    setDefaultUsernameTakenMessage(taken);
  };

  const handleDefaultUsernameBlur = (e) => {
    const err = getUsernameValidationError(e.target.value);
    setDefaultUsernameError(err);
    setDefaultUsernameTakenMessage(getUsernameTakenMessage(e.target.value, existingUsernames));
  };

  const handleDefaultNameChange = (e) => {
    const value = e.target.value;
    setDefaultUserName(value);
    setDefaultNameError(getNameValidationError(value));
  };

  const handleDefaultNameBlur = (e) => {
    setDefaultNameError(getNameValidationError(e.target.value));
  };

  const handleDefaultUserEmailChange = (e) => {
    setDefaultUserEmail(e.target.value);
    setSignupEmailToken('');
    setSignupEmailOtp('');
    setEmailVerifiedForCreate(false);
    setFormErrors((prev) => ({ ...prev, defaultUserEmail: '', defaultUserEmailVerify: '' }));
  };

  const sendDefaultUserSignupEmailCode = async () => {
    if (!defaultUserEmail.trim() || validateEmail(defaultUserEmail)) {
      toast.error('Enter a valid email before sending a code.');
      return;
    }
    setSignupEmailBusy(true);
    try {
      await sendSignupEmailCode(defaultUserEmail);
      setSignupEmailToken('');
      setEmailVerifiedForCreate(false);
      toast.success('Verification code sent. Check the inbox for this email.');
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Could not send verification code');
    } finally {
      setSignupEmailBusy(false);
    }
  };

  const verifyDefaultUserSignupEmailOtp = async () => {
    if (!defaultUserEmail.trim() || validateEmail(defaultUserEmail)) {
      toast.error('Enter a valid email.');
      return;
    }
    if (!signupEmailOtp || signupEmailOtp.length !== 6) {
      toast.error('Enter the 6-digit code from the email.');
      return;
    }
    setSignupEmailBusy(true);
    try {
      const token = await verifySignupEmailOtp(defaultUserEmail, signupEmailOtp);
      if (token) {
        setSignupEmailToken(token);
        setEmailVerifiedForCreate(true);
        setFormErrors((prev) => ({ ...prev, defaultUserEmailVerify: '' }));
        toast.success('Email verified. You can submit the form to create the company.');
      } else {
        toast.error('Unexpected response from server');
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Verification failed');
      setEmailVerifiedForCreate(false);
      setSignupEmailToken('');
    } finally {
      setSignupEmailBusy(false);
    }
  };

  /**
   * Per-step validation for the wizard's Next button. Each function returns
   * `true` when the step's fields are complete + valid, and populates
   * `formErrors` (inline messages) + toasts a first-missing-field prompt
   * so the user sees why Next was blocked. Shares the same field-level
   * rules as `submitForm` — nothing new added at the schema level.
   */
  function validateStep0Details() {
    const errs: Record<string, string> = {};
    let ok = true;
    let firstMissing = '';
    if (!companyName.trim()) {
      ok = false;
      firstMissing ||= 'Company name is required';
    }
    if (!companyPhone.trim() || !/^[0-9]{10}$/.test(companyPhone.trim())) {
      ok = false;
      firstMissing ||= 'Enter a valid 10-digit phone number';
    }
    if (!companyPincode.trim() || !/^\d{6}$/.test(companyPincode)) {
      ok = false;
      firstMissing ||= 'Enter a valid 6-digit pincode';
    }
    if (!companyAddress.trim()) {
      ok = false;
      errs.companyAddress = 'Company address is required';
      firstMissing ||= 'Company address is required';
    }
    if (!companyExpiryDate) {
      ok = false;
      errs.companyExpiry = 'Company expiry date is required';
      firstMissing ||= 'Company expiry date is required';
    } else if (parentExpiryDate && !isDemoCompany) {
      const c = new Date(companyExpiryDate);
      const p = new Date(parentExpiryDate);
      c.setHours(0, 0, 0, 0);
      p.setHours(0, 0, 0, 0);
      if (c.getTime() > p.getTime()) {
        ok = false;
        errs.companyExpiry = "Company expiry cannot exceed parent company's expiry date";
        firstMissing ||= errs.companyExpiry;
      }
    }
    setFormErrors((prev) => ({ ...prev, ...errs }));
    if (!ok && firstMissing) toast.warning(firstMissing);
    return ok;
  }

  function validateStep1Setup() {
    const errs: Record<string, string> = {};
    let ok = true;
    let firstMissing = '';
    if (!isResellerCompany && !isOwnCompany) {
      ok = false;
      errs.companyType = 'Please select either Reseller Company or End User';
      firstMissing ||= errs.companyType;
    }
    const billingError = billingPlanFormError(
      billingType,
      postpaidMode,
      parentIsPrepaid,
      parentPostpaidCredit,
      limitPeriod,
      limitPeriod === 'daily' ? dailyLimit : limitPeriod === 'weekly' ? weeklyLimit : monthlyLimit,
      parentLimitPeriod || null,
    );
    if (billingError) {
      ok = false;
      errs.billingPlan = billingError;
      firstMissing ||= billingError;
    }
    if (ttsPlanIncomplete) {
      ok = false;
      errs.ttsPlan = ttsPlanErrorMessage;
      firstMissing ||= errs.ttsPlan;
    }
    setFormErrors((prev) => ({ ...prev, ...errs }));
    if (!ok && firstMissing) toast.warning(firstMissing);
    return ok;
  }

  function validateStep2Licence() {
    const licenceErrs: Record<string, string> = {};
    let ok = true;
    let firstMissing = '';
    if (Object.values(errors).some((e) => e !== '')) {
      ok = false;
      firstMissing ||= 'Fix the licence quotas before continuing';
    }
    if (Number(liveAgent) > Number(agent)) {
      ok = false;
      licenceErrs.live_agent = 'Live Agent cannot exceed Total Agent';
      firstMissing ||= licenceErrs.live_agent;
    }
    if (isDemoCompany && Number(channel) > Number(liveAgent) * 2) {
      ok = false;
      licenceErrs.channel = 'Channels cannot exceed Total Live Agent * 2';
      firstMissing ||= licenceErrs.channel;
    }
    if (isDemoCompany && Number(teamLead) > Number(agent)) {
      ok = false;
      licenceErrs.team_lead = 'Total Team Lead cannot exceed Total Agent';
      firstMissing ||= licenceErrs.team_lead;
    }
    if (isDemoCompany && demoSettings) {
      if (Number(agent) > Number(demoSettings.total_demo_agent_per_company)) {
        ok = false;
        licenceErrs.agent = `Total Agent cannot exceed ${demoSettings.total_demo_agent_per_company}`;
        firstMissing ||= licenceErrs.agent;
      }
      if (Number(liveAgent) > Number(demoSettings.total_demo_live_agent_per_company)) {
        ok = false;
        licenceErrs.live_agent = `Total Live Agent cannot exceed ${demoSettings.total_demo_live_agent_per_company}`;
        firstMissing ||= licenceErrs.live_agent;
      }
    }
    if (Object.keys(licenceErrs).length > 0) {
      setErrors((prev) => ({ ...prev, ...licenceErrs }));
    }
    if (!ok && firstMissing) toast.warning(firstMissing);
    return ok;
  }

  function handleWizardNext() {
    if (stepper.current === 0) {
      const ok = validateStep0Details();
      // Force every inline error to surface so the user sees which fields
      // are missing, not just the toast for the first one.
      setForceShowStep0Errors(!ok);
      if (!ok) return;
    }
    if (stepper.current === 1 && !validateStep1Setup()) return;
    if (stepper.current === 2 && !validateStep2Licence()) return;
    if (stepper.current === 3 && domainError) return;
    stepper.next();
  }

  function validateCompanyPhone(phone) {
    if (!phone) return false;
    return /^[0-9]{10}$/.test(phone.trim());
  }

  const handleLogoChange = (e) =>
    handleCompanyLogoChange(e, { setLogoFile, setLogoPreview, setLogoError });

  const uploadLogo = async () => {
    if (!logoFile) return null;
    const formData = new FormData();
    formData.append('logo', logoFile);
    try {
      const rawUser = Storage.getItem('user');
      const userData = rawUser ? JSON.parse(rawUser) : null;
      const token = userData?.token;
      if (!token) {
        toast.error('Session expired. Please login again.');
        return null;
      }
      const url = `${BACKEND_API_BASE_URL}${API_COMPANY_UPLOAD_LOGO}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) return null;
      return data.logo_path || data.path || data.filePath || data.data?.logo_path || null;
    } catch (err) {
      return null;
    }
  };

  function resetTtsPlanSelection() {
    setTtsPlanId('');
    setTtsPlanMode('existing');
    setNewTtsPlanName('');
    setNewTtsUnitBalance(TTS_UNIT_RUPEES);
    setNewTtsPerCharacter('');
    setNewTtsPerVariable('');
    setFormErrors((prev) => ({ ...prev, ttsPlan: '' }));
  }

  async function submitForm() {
    if (!canSubmitForm || isSubmitting) return;

    setIsSubmitting(true);
    setFormErrors({});

    if (!validateCompanyFields()) {
      setIsSubmitting(false);
      return;
    }
    if (createDefaultUser && !validateDefaultUserFields()) {
      setIsSubmitting(false);
      return;
    }
    if (resellerCreationBlocked && isResellerCompany) {
      setFormErrors((prev) => ({
        ...prev,
        companyType:
          'You are not allowed to create a Reseller Company. Please create an End User.',
      }));
      setIsSubmitting(false);
      return;
    }
    if (liveAgent > agent) {
      setErrors((prev) => ({ ...prev, live_agent: 'Live Agent cannot exceed Total Agents' }));
      setIsSubmitting(false);
      return;
    }
    if (Object.values(errors).some((error) => error !== '')) {
      setIsSubmitting(false);
      return;
    }

    let finalLogoPath = logoPath;
    if (logoFile) {
      const uploaded = await uploadLogo();
      if (uploaded) finalLogoPath = uploaded;
    }

    let ttsPlanIdForCompany = tts_plan_id;
    if (applyTtsPlan && ttsPlanMode === 'new') {
      try {
        const createdPlan = await postApiCall(API_TTS_PLANS_CREATE, {
          plan_name: newTtsPlanName.trim(),
          per_character: Number(newTtsPerCharacter),
          per_variable: Number(newTtsPerVariable),
          unit_balance: newTtsUnitBalance,
        });
        if (!createdPlan?.id) throw 'TTS plan was created without an id';
        ttsPlanIdForCompany = createdPlan.id;
        setTtsPlanList((prev) => [...prev, createdPlan]);
        setTtsPlanId(String(createdPlan.id));
        setTtsPlanMode('existing');
        toast.success('TTS plan created successfully');
      } catch (err) {
        setIsSubmitting(false);
        toast.error(friendlyApiError(err) || 'Failed to create TTS plan');
        return;
      }
    }

    const params = {
      name: companyName,
      phone: companyPhone,
      address: companyAddress,
      pincode: companyPincode,
      is_reseller_company: isResellerCompany,
      is_own_company: isOwnCompany,
      is_demo_company: isDemoCompany,
      billing_type: isDemoCompany ? 'prepaid' : billingType,
      ...(!isDemoCompany && billingType === 'postpaid'
        ? {
            postpaid_mode: postpaidMode,
            credit_limit:
              postpaidMode === 'unlimited'
                ? POSTPAID_CREDIT_MAX
                : Number(limitPeriod === 'daily' ? dailyLimit : limitPeriod === 'weekly' ? weeklyLimit : monthlyLimit),
            ...(postpaidMode === 'limited' ? { billing_cycle: limitPeriod } : {}),
          }
        : {}),
      expiry_date: companyExpiryDate,
      domain: domain.trim() || null,
      panel_display_name: panelDisplayName.trim() || null,
      logo_path: finalLogoPath || null,
      licence: {
        role_ccadmin: callCenterAdminTeamlead,
        role_ccagent: agent,
        role_teamlead: teamLead,
        live_agent: liveAgent,
        channel: channel,
        cps: cps,
        mobile: mobile,
        reseller: 0,
        company: isOwnCompany ? 0 : asCompany,
        demo_live_agents_per_reseller_company: demoLiveAgents,
        ai_agent: aiAgent,
        ai_agent_live_channel: aiAgentLiveChannel,
      },
    };

    if (selectedGroup && selectedGroup.length > 0) {
      params.groups = Array.isArray(selectedGroup)
        ? selectedGroup.map(Number)
        : [Number(selectedGroup)];
    }
    if (selectedLocation && selectedLocation.length > 0) {
      params.locations = Array.isArray(selectedLocation)
        ? selectedLocation.map(Number)
        : [Number(selectedLocation)];
    }
    if (selectedCallStrategy && selectedCallStrategy.length > 0) {
      params.call_strategies = Array.isArray(selectedCallStrategy)
        ? selectedCallStrategy.map(Number)
        : [Number(selectedCallStrategy)];
    }
    if (selectedCountryCodeDirections && selectedCountryCodeDirections.length > 0) {
      params.country_code_directions = Array.isArray(selectedCountryCodeDirections)
        ? selectedCountryCodeDirections.map(Number)
        : [Number(selectedCountryCodeDirections)];
    }

    if (createDefaultUser) {
      params.default_user = {
        name: defaultUserName.trim(),
        username: defaultUsername.trim(),
        email: defaultUserEmail.trim(),
        password: defaultUserPassword,
        confirm_password: defaultUserConfirmPassword,
        expiry_date: defaultUserExpiryDate,
        acl_settings: defaultUserAcl,
        ...(defaultUserEmailVerificationRequired && signupEmailToken
          ? { signup_email_token: signupEmailToken }
          : {}),
        role: defaultUserRoleSelection,
      };
      if (isCreatorSuperAdmin) {
        params.default_user.account_type = Number(defaultUserAccountType);
      }
    }

    if (applyTtsPlan && ttsPlanIdForCompany) {
      params.tts_plan_id = ttsPlanIdForCompany;
    }

    postApiCall(API_COMPANY_CREATE, params).then(onCreateCreateSuccess).catch(onError);
  }

  function onCreateCreateSuccess(resp) {
    toast.success(
      createDefaultUser
        ? 'Company and default user created successfully'
        : 'Company created successfully',
    );
    setTimeout(() => {
      navigate(`/${ROUTES.COMPANIES.LIST}`);
    }, 1500);
  }

  function onError(err) {
    setIsSubmitting(false);
    console.log(err);
    toast.error(friendlyApiError(err) || 'Failed to create company');
  }

  return (
    <FillPage>
      <PageHeader
        title="New Company"
        description="Provision a tenant company with credits, default user, and address details."
        actions={
          <ShadPageButton asChild variant="outline">
            <RouterLink to={`/${PAGE_ROUTES.COMPANIES.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back to companies
            </RouterLink>
          </ShadPageButton>
        }
      />
      <Row>
        <Col xl="12" lg="12">
          <ShadcnCard className="mt-4">
            <div className="p-4">
              <div className="new-user-info company-create-form">
                <Stepper
                  steps={CREATE_COMPANY_STEPS}
                  current={stepper.current}
                  completed={stepper.completed}
                  onStepClick={stepper.goTo}
                  className="mb-6"
                />
                <form>
                {/* ── Step 0: Details ─────────────────────────────── */}
                {stepper.current === 0 && (
                  <>
                  {/* Rendered as JSX (not called as a function) so
                      RenderUserCompanyFields's internal useState hooks
                      live in their own component instance. Calling it
                      like `{RenderUserCompanyFields({...})}` inside a
                      step conditional would make its hooks belong to
                      CompanyCreate and disappear on step change,
                      which trips React's "rendered fewer hooks"
                      invariant. */}
                  <RenderUserCompanyFields
                    showAllErrors={forceShowStep0Errors}
                    company_name={companyName}
                    set_company_name={setCompanyName}
                    company_phone={companyPhone}
                    set_company_phone={setCompanyPhone}
                    company_address={companyAddress}
                    set_company_address={(val) => {
                      setCompanyAddress(val);
                      if (formErrors.companyAddress) {
                        setFormErrors((prev) => ({ ...prev, companyAddress: '' }));
                      }
                    }}
                    company_address_error={formErrors.companyAddress}
                    company_pincode={companyPincode}
                    set_company_pincode={setCompanyPincode}
                    locations={locations}
                    selectedLocation={selectedLocation}
                    setSelectedLocation={setSelectedLocation}
                    countryCodeDirections={countryCodeDirections}
                    selectedCountryCodeDirections={selectedCountryCodeDirections}
                    setSelectedCountryCodeDirections={setSelectedCountryCodeDirections}
                  />

                  <div className="row g-3 mt-1">
                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label>
                        Company Expiry Date <span className="text-danger">*</span>
                      </Form.Label>
                      <CalendarDateInput
                        value={companyExpiryDate}
                        onChange={(e) => {
                          setCompanyExpiryDate(e);
                          if (formErrors.companyExpiry) {
                            setFormErrors((prev) => ({ ...prev, companyExpiry: '' }));
                          }
                        }}
                        min={todayInputString()}
                        max={
                          !isDemoCompany && parentExpiryDate
                            ? toDateInputString(parentExpiryDate)
                            : undefined
                        }
                        className={formErrors.companyExpiry ? 'is-invalid' : ''}
                        disabled={isDemoCompany}
                      />
                      {formErrors.companyExpiry ? (
                        <div className="invalid-feedback d-block">{formErrors.companyExpiry}</div>
                      ) : null}
                      <small className="text-muted d-block mt-1">
                        {isDemoCompany
                          ? `Managed automatically for demo companies (${demoExpiryDays || 'N'} day(s) per recharge, extended on each recharge). All users inherit it.`
                          : 'All users of this company inherit this expiry date.'}
                      </small>
                    </Form.Group>
                  </div>
                  </>
                )}

                {/* ── Step 1: Setup ───────────────────────────────── */}
                {stepper.current === 1 && (
                  <>
                  <div className="row g-3 mt-1">
                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label className="d-flex align-items-center w-100">
                        Call Strategy
                        <SelectAllToggle
                          allIds={callStrategies.map((o) => o.value)}
                          selectedIds={selectedCallStrategy}
                          onChange={setSelectedCallStrategy}
                        />
                      </Form.Label>
                      <Select
                        className="theme-react-select"
                        classNamePrefix="theme-react-select"
                        options={callStrategies}
                        value={callStrategies.filter((l) => selectedCallStrategy.includes(l.value))}
                        onChange={(selected) =>
                          setSelectedCallStrategy(selected.map((s) => s.value))
                        }
                        isMulti
                        placeholder="Select Call Strategy"
                      />
                    </Form.Group>
                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label className="d-flex align-items-center w-100">
                        Dialer Group
                        <SelectAllToggle
                          allIds={groups.map((o) => o.value)}
                          selectedIds={selectedGroup}
                          onChange={setSelectedGroup}
                        />
                      </Form.Label>
                      <Select
                        className="theme-react-select"
                        classNamePrefix="theme-react-select"
                        options={groups}
                        value={groups.filter((g) => selectedGroup.includes(g.value))}
                        onChange={(selected) => setSelectedGroup(selected.map((s) => s.value))}
                        isMulti
                        placeholder="Select Dialer Group"
                      />
                    </Form.Group>
                  </div>

                  <div className="company-type-section-v2 mt-3">
                    <Form.Label className="ccf-field-label d-block">Apply TTS Plan</Form.Label>
                    <div className="row g-3 align-items-end">
                      <div className="col-12 col-md-6">
                        <div
                          role="button"
                          tabIndex={0}
                          className={`company-type-card-v2 ${applyTtsPlan ? 'is-selected' : ''}`}
                          onClick={() => {
                            const next = !applyTtsPlan;
                            setApplyTtsPlan(next);
                            if (!next) resetTtsPlanSelection();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              const next = !applyTtsPlan;
                              setApplyTtsPlan(next);
                              if (!next) resetTtsPlanSelection();
                            }
                          }}
                        >
                          <span className="company-type-card-v2__radio" aria-hidden="true" />
                          <div className="company-type-card-v2__body">
                            <div className="company-type-card-v2__title">Apply TTS Plan</div>
                            <div className="company-type-card-v2__desc">
                              Assign a TTS plan to this company
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {applyTtsPlan && (
                      <>
                        {ttsPlansAcl.canAdd && (
                          <div className="row g-3 mt-2">
                            <Form.Group className="col-12 col-md-6 form-group mb-0">
                              <Form.Label>TTS Plan Source</Form.Label>
                              <Form.Select
                                value={ttsPlanMode}
                                className="p-2"
                                onChange={(e) => {
                                  setTtsPlanMode(e.target.value);
                                  setFormErrors((prev) => ({ ...prev, ttsPlan: '' }));
                                }}
                              >
                                <option value="existing">Use an existing TTS plan</option>
                                <option value="new">Create a new TTS plan</option>
                              </Form.Select>
                            </Form.Group>
                          </div>
                        )}

                        {ttsPlanMode === 'new' && ttsPlansAcl.canAdd ? (
                          <div className="row g-3 mt-2">
                            <Form.Group className="col-12 col-md-6 col-lg-3 form-group mb-0">
                              <Form.Label>
                                Plan Name <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                value={newTtsPlanName}
                                placeholder="Plan Name"
                                className={`p-2${formErrors.ttsPlan ? ' is-invalid' : ''}`}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (TTS_PLAN_NAME_REGEX.test(value)) {
                                    setNewTtsPlanName(value);
                                    if (formErrors.ttsPlan) {
                                      setFormErrors((prev) => ({ ...prev, ttsPlan: '' }));
                                    }
                                  }
                                }}
                              />
                            </Form.Group>
                            <Form.Group className="col-12 col-md-6 col-lg-3 form-group mb-0">
                              <Form.Label>
                                Unit Balance <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Select
                                value={newTtsUnitBalance}
                                className="p-2"
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setNewTtsUnitBalance(next);
                                  if (next === TTS_UNIT_RUPEES) {
                                    setNewTtsPerCharacter((prev) => toWholeTtsPrice(prev));
                                    setNewTtsPerVariable((prev) => toWholeTtsPrice(prev));
                                  }
                                }}
                              >
                                <option value={TTS_UNIT_RUPEES}>Rupees</option>
                                <option value={TTS_UNIT_PAISE}>Paise</option>
                              </Form.Select>
                            </Form.Group>
                            <Form.Group className="col-12 col-md-6 col-lg-3 form-group mb-0">
                              <Form.Label>
                                Per Character <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                inputMode={newTtsAllowDecimals ? 'decimal' : 'numeric'}
                                value={newTtsPerCharacter}
                                placeholder={newTtsAllowDecimals ? 'e.g. 0.05' : '1 – 1000'}
                                className={`p-2${formErrors.ttsPlan ? ' is-invalid' : ''}`}
                                onChange={(e) => {
                                  const next = sanitizeTtsPriceInput(
                                    e.target.value,
                                    newTtsAllowDecimals,
                                  );
                                  if (next !== null) {
                                    setNewTtsPerCharacter(next);
                                    if (formErrors.ttsPlan) {
                                      setFormErrors((prev) => ({ ...prev, ttsPlan: '' }));
                                    }
                                  }
                                }}
                                onKeyDown={(e) => {
                                  const blocked = newTtsAllowDecimals
                                    ? ['e', 'E', '+', '-']
                                    : ['e', 'E', '+', '-', '.'];
                                  if (blocked.includes(e.key)) e.preventDefault();
                                }}
                              />
                            </Form.Group>
                            <Form.Group className="col-12 col-md-6 col-lg-3 form-group mb-0">
                              <Form.Label>
                                Per Variable <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                inputMode={newTtsAllowDecimals ? 'decimal' : 'numeric'}
                                value={newTtsPerVariable}
                                placeholder={newTtsAllowDecimals ? 'e.g. 0.05' : '1 – 1000'}
                                className={`p-2${formErrors.ttsPlan ? ' is-invalid' : ''}`}
                                onChange={(e) => {
                                  const next = sanitizeTtsPriceInput(
                                    e.target.value,
                                    newTtsAllowDecimals,
                                  );
                                  if (next !== null) {
                                    setNewTtsPerVariable(next);
                                    if (formErrors.ttsPlan) {
                                      setFormErrors((prev) => ({ ...prev, ttsPlan: '' }));
                                    }
                                  }
                                }}
                                onKeyDown={(e) => {
                                  const blocked = newTtsAllowDecimals
                                    ? ['e', 'E', '+', '-']
                                    : ['e', 'E', '+', '-', '.'];
                                  if (blocked.includes(e.key)) e.preventDefault();
                                }}
                              />
                            </Form.Group>
                            {newTtsFloorError || formErrors.ttsPlan ? (
                              <div className="col-12">
                                <div className="invalid-feedback d-block">
                                  {newTtsFloorError || formErrors.ttsPlan}
                                </div>
                              </div>
                            ) : null}
                            <div className="col-12">
                              <small className="text-muted">
                                {/* This plan is created first and then assigned to the new company. */}
                                {ttsPlanFloor
                                  ? ` Prices cannot be lower than ${ttsPlanFloor.per_character} / ${ttsPlanFloor.per_variable} ${ttsPlanFloor.unit_balance}.`
                                  : ''}
                              </small>
                            </div>
                          </div>
                        ) : (
                          <div className="row g-3 mt-2">
                            <Form.Group className="col-12 col-md-6 form-group mb-0">
                              <Form.Label>
                                Select TTS Plan <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Select
                                value={tts_plan_id}
                                className={`p-2${formErrors.ttsPlan ? ' is-invalid' : ''}`}
                                onChange={(e) => {
                                  setTtsPlanId(e.target.value);
                                  if (formErrors.ttsPlan) {
                                    setFormErrors((prev) => ({ ...prev, ttsPlan: '' }));
                                  }
                                }}
                              >
                                <option value="">Select TTS Plan</option>
                                {ttsPlanList.map((val, idx) => (
                                  <option key={idx} value={val.id}>
                                    {val.plan_name}
                                  </option>
                                ))}
                              </Form.Select>
                              {formErrors.ttsPlan ? (
                                <div className="invalid-feedback d-block">{formErrors.ttsPlan}</div>
                              ) : null}
                            </Form.Group>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="company-type-section-v2 mt-3">
                    <Form.Label className="ccf-field-label d-block">Demo Company</Form.Label>
                    <div className="row g-3 align-items-end">
                      <div className="col-12 col-md-6">
                        <div
                          role="button"
                          tabIndex={0}
                          className={`company-type-card-v2 ${isDemoCompany ? 'is-selected' : ''}`}
                          onClick={() => handleDemoCompanyChange(!isDemoCompany)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleDemoCompanyChange(!isDemoCompany);
                            }
                          }}
                          style={forcedDemo ? { opacity: 0.7, pointerEvents: 'none' } : undefined}
                        >
                          <span className="company-type-card-v2__radio" aria-hidden="true" />
                          <div className="company-type-card-v2__body">
                            <div className="company-type-card-v2__title">Demo Company</div>
                            <div className="company-type-card-v2__desc">
                              {forcedDemo
                                ? 'Your company is a demo company, so this company will be a demo company'
                                : 'Mark this company as a demo company (end user only)'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="company-type-section-v2 mt-3">
                    <Form.Label className="ccf-field-label d-block">
                      Company type <span className="text-danger">*</span>
                    </Form.Label>
                    {formErrors.companyType ? (
                      <div className="text-danger small mb-2">{formErrors.companyType}</div>
                    ) : null}
                    <div className="row g-3">
                      {!resellerCreationBlocked && !isDemoCompany && (
                        <div className="col-12 col-md-6">
                          <div
                            role="button"
                            tabIndex={0}
                            className={`company-type-card-v2 ${
                              isResellerCompany ? 'is-selected' : ''
                            }`}
                            onClick={() => handleResellerChange(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleResellerChange(true);
                              }
                            }}
                          >
                            <span className="company-type-card-v2__radio" aria-hidden="true" />
                            <span className="company-type-card-v2__icon company-type-card-v2__icon--reseller">
                              <FaBuilding size={20} />
                            </span>
                            <div className="company-type-card-v2__body">
                              <div className="company-type-card-v2__title">Reseller Company</div>
                              <div className="company-type-card-v2__desc">
                                Can create and manage child companies
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div
                        className={
                          resellerCreationBlocked || isDemoCompany ? 'col-12' : 'col-12 col-md-6'
                        }
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          className={`company-type-card-v2 ${isOwnCompany ? 'is-selected' : ''}`}
                          onClick={() => handleOwnCompanyChange(true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleOwnCompanyChange(true);
                            }
                          }}
                        >
                          <span className="company-type-card-v2__radio" aria-hidden="true" />
                          <span className="company-type-card-v2__icon company-type-card-v2__icon--enduser">
                            <FiUsers size={22} />
                          </span>
                          <div className="company-type-card-v2__body">
                            <div className="company-type-card-v2__title">End User</div>
                            <div className="company-type-card-v2__desc">
                              Operates as a single end-user company
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {!isDemoCompany && (
                  <div className="company-type-section-v2 mt-3">
                    <div className="row g-3">
                      <div className="col-12 col-lg-6">
                        <div className="billing-plan-fields">
                          <div>
                            <Form.Label className="ccf-field-label d-block">
                              Billing Plans <span className="text-danger">*</span>
                            </Form.Label>
                            {formErrors.billingPlan ? (
                              <div className="text-danger small mb-2">{formErrors.billingPlan}</div>
                            ) : null}
                            {parentIsPrepaid ? (
                              <div className="text-muted small mb-2">
                                Your company is prepaid, so you can only create a prepaid company.
                              </div>
                            ) : null}
                            <Form.Select
                              aria-label="Billing Plans"
                              value={billingType}
                              className={formErrors.billingPlan ? 'is-invalid' : ''}
                              onChange={(e) => {
                                const next = e.target.value;
                                setBillingType(next);
                                setPostpaidMode('');
                                setCreditLimit('');
                                setLimitPeriod('');
                                setDailyLimit('');
                                setWeeklyLimit('');
                                setMonthlyLimit('');
                                if (formErrors.billingPlan) {
                                  setFormErrors((prev) => ({ ...prev, billingPlan: '' }));
                                }
                              }}
                            >
                              <option value="">Select billing plan</option>
                              {!parentIsPrepaid && <option value="postpaid">Postpaid</option>}
                              <option value="prepaid">Prepaid</option>
                            </Form.Select>
                          </div>
                          {billingType === 'postpaid' && (
                            <div>
                              <Form.Label className="ccf-field-label d-block">
                                Postpaid plan <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Select
                                aria-label="Postpaid plan"
                                value={postpaidMode}
                                className={formErrors.billingPlan ? 'is-invalid' : ''}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setPostpaidMode(next);
                                  setCreditLimit(next === 'unlimited' ? String(POSTPAID_CREDIT_MAX) : '');
                                  if (next === 'limited' && parentLimitPeriod) {
                                    setLimitPeriod(parentLimitPeriod);
                                  } else {
                                    setLimitPeriod('');
                                    setDailyLimit('');
                                    setWeeklyLimit('');
                                    setMonthlyLimit('');
                                  }
                                  if (formErrors.billingPlan) {
                                    setFormErrors((prev) => ({ ...prev, billingPlan: '' }));
                                  }
                                }}
                              >
                                <option value="">Select postpaid plan</option>
                                {!parentLimitPeriod && postpaidCreditCeiling(parentPostpaidCredit) >= POSTPAID_CREDIT_MAX && (
                                  <option value="unlimited">Unlimited Postpaid (10 lac)</option>
                                )}
                                <option value="limited">Limited Postpaid</option>
                              </Form.Select>
                              {postpaidMode === 'unlimited' ? (
                                <small className="text-muted">
                                  Voice, TTS, and AI each start at 10 lac (10,00,000). A voice charge reduces only voice credits. The parent company's credits stay the same.
                                </small>
                              ) : null}
                            </div>
                          )}
                          {billingType === 'postpaid' && postpaidMode === 'limited' && (
                            <div>
                              <Form.Label className="ccf-field-label d-block">
                                Limit period <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Select
                                aria-label="Limit period"
                                value={limitPeriod}
                                className={formErrors.billingPlan ? 'is-invalid' : ''}
                                onChange={(e) => {
                                  setLimitPeriod(e.target.value);
                                  if (formErrors.billingPlan) {
                                    setFormErrors((prev) => ({ ...prev, billingPlan: '' }));
                                  }
                                }}
                              >
                                <option value="">Select daily, weekly, or monthly</option>
                                {(!parentLimitPeriod || parentLimitPeriod === 'daily') && (
                                  <option value="daily">Daily</option>
                                )}
                                {(!parentLimitPeriod || parentLimitPeriod === 'weekly') && (
                                  <option value="weekly">Weekly</option>
                                )}
                                {(!parentLimitPeriod || parentLimitPeriod === 'monthly') && (
                                  <option value="monthly">Monthly</option>
                                )}
                              </Form.Select>
                            </div>
                          )}
                          {billingType === 'postpaid' && postpaidMode === 'limited' && limitPeriod && (
                            <div>
                              <Form.Label className="ccf-field-label d-block">
                                {limitPeriod === 'daily'
                                  ? 'Daily limit'
                                  : limitPeriod === 'weekly'
                                    ? 'Weekly limit'
                                    : 'Monthly limit'}{' '}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                value={
                                  limitPeriod === 'daily'
                                    ? dailyLimit
                                    : limitPeriod === 'weekly'
                                      ? weeklyLimit
                                      : monthlyLimit
                                }
                                placeholder={`Up to ${formatCreditLimit(postpaidCreditCeiling(parentPostpaidCredit))}`}
                                className={formErrors.billingPlan ? 'is-invalid' : ''}
                                onChange={(e) => {
                                  const cap = postpaidCreditCeiling(parentPostpaidCredit);
                                  const setValue =
                                    limitPeriod === 'daily'
                                      ? setDailyLimit
                                      : limitPeriod === 'weekly'
                                        ? setWeeklyLimit
                                        : setMonthlyLimit;
                                  const digits = e.target.value.replace(/\D/g, '');
                                  setValue(digits ? String(Math.min(Number(digits), cap)) : '');
                                  if (formErrors.billingPlan) {
                                    setFormErrors((prev) => ({ ...prev, billingPlan: '' }));
                                  }
                                }}
                              />
                              <small className="text-muted">
                                {parentLimitPeriod
                                  ? `This company can only use a ${parentLimitPeriod} limit, up to ${formatCreditLimit(postpaidCreditCeiling(parentPostpaidCredit))}. Voice, TTS, and AI each start at this amount.`
                                  : `Enter one whole number up to ${formatCreditLimit(postpaidCreditCeiling(parentPostpaidCredit))}. Voice, TTS, and AI each start at this amount.`}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                  </>
                )}

                {/* ── Step 2: Licence ─────────────────────────────── */}
                {stepper.current === 2 && (
                  <>
                  {isDemoCompany && demoSettings && (isResellerCompany || isOwnCompany) && (
                    <div className="licence-available-summary" style={{ marginBottom: 12 }}>
                      <h6 className="licence-available-summary__title">Demo company licence</h6>
                      <div>
                        Agents, Live Agents, Team Leads, Call Centers and Channels are fixed to the
                        per-company demo limits. CPS, Mobile and Company are editable.
                      </div>
                    </div>
                  )}

                  {(isResellerCompany || isOwnCompany) && (
                    <LicenceAllocationPanel
                      showNote={!isOwnCompany}
                      noteMessage={
                        notes.find((n) => Number(n.priority) === 1)?.message ??
                        'One Company Licence is automatically used for the newly created company.'
                      }
                      footerNote={
                        loginUserRole !== USER_ROLES.SUPERADMIN
                          ? notes.find((n) => Number(n.priority) === 2)?.message ?? ''
                          : ''
                      }
                      columns={[
                        {
                          key: 'role_ccadmin',
                          icon: FaHeadset,
                          iconModifier: 'callcenter',
                          title: 'Call Centers',
                          description: 'Number of call centers',
                          value: parseInt(String(callCenterAdminTeamlead), 10) || 0,
                          onChange: (val) =>
                            setCallCenterAdminTeamlead(validateInput(String(val), 'role_ccadmin')),
                          onDecrement: () =>
                            stepLicence('role_ccadmin', callCenterAdminTeamlead, setCallCenterAdminTeamlead, -1),
                          onIncrement: () =>
                            stepLicence('role_ccadmin', callCenterAdminTeamlead, setCallCenterAdminTeamlead, 1),
                          disabledMinus: (parseInt(String(callCenterAdminTeamlead), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'role_ccadmin',
                            parseInt(String(callCenterAdminTeamlead), 10) || 0,
                          ),
                          error: errors.role_ccadmin,
                          readOnly: demoLicenceLocked,
                        },
                        {
                          key: 'agent',
                          icon: FiUsers,
                          iconModifier: 'agent',
                          title: 'Total Agent',
                          description: 'Total number of agents',
                          value: parseInt(String(agent), 10) || 0,
                          onChange: (val) => setAgent(validateInput(String(val), 'agent')),
                          onDecrement: () => stepLicence('agent', agent, setAgent, -1),
                          onIncrement: () => stepLicence('agent', agent, setAgent, 1),
                          disabledMinus: (parseInt(String(agent), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence('agent', parseInt(String(agent), 10) || 0),
                          error: errors.agent,
                          readOnly: demoLicenceLocked,
                        },
                        {
                          key: 'team_lead',
                          icon: FiUserCheck,
                          iconModifier: 'agent',
                          title: 'Total Team Lead',
                          description: 'Total number of team leads',
                          value: parseInt(String(teamLead), 10) || 0,
                          onChange: (val) => setTeamLead(validateInput(String(val), 'team_lead')),
                          onDecrement: () => stepLicence('team_lead', teamLead, setTeamLead, -1),
                          onIncrement: () => stepLicence('team_lead', teamLead, setTeamLead, 1),
                          disabledMinus: (parseInt(String(teamLead), 10) || 0) <= 0,
                          disabledPlus:
                            !canIncrementLicence('team_lead', parseInt(String(teamLead), 10) || 0) ||
                            (parseInt(String(teamLead), 10) || 0) >= (parseInt(String(agent), 10) || 0),
                          error: errors.team_lead,
                          readOnly: demoLicenceLocked,
                        },
                        {
                          key: 'live_agent',
                          icon: FiActivity,
                          iconModifier: 'live',
                          title: 'Live Agent',
                          description: 'Agents for live calls',
                          value: parseInt(String(liveAgent), 10) || 0,
                          onChange: (val) => setLiveAgent(validateInput(String(val), 'live_agent')),
                          onDecrement: () => stepLicence('live_agent', liveAgent, setLiveAgent, -1),
                          onIncrement: () => stepLicence('live_agent', liveAgent, setLiveAgent, 1),
                          disabledMinus: (parseInt(String(liveAgent), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'live_agent',
                            parseInt(String(liveAgent), 10) || 0,
                          ),
                          error: errors.live_agent,
                          readOnly: demoLicenceLocked,
                        },
                        {
                          key: 'channel',
                          icon: FiShare2,
                          iconModifier: 'channel',
                          title: 'Channels',
                          description: 'Communication channels',
                          value: parseInt(String(channel), 10) || 0,
                          onChange: (val) => setChannel(validateInput(String(val), 'channel')),
                          onDecrement: () => stepLicence('channel', channel, setChannel, -1),
                          onIncrement: () => stepLicence('channel', channel, setChannel, 1),
                          disabledMinus: (parseInt(String(channel), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'channel',
                            parseInt(String(channel), 10) || 0,
                          ),
                          error: errors.channel,
                          readOnly: demoLicenceLocked,
                        },
                        {
                          key: 'ai_agent',
                          icon: FiCpu,
                          iconModifier: 'ai',
                          title: 'AI Agents',
                          description: 'AI agents this call centre can create',
                          value: parseInt(String(aiAgent), 10) || 0,
                          onChange: (val) => setAiAgent(validateInput(String(val), 'ai_agent')),
                          onDecrement: () => stepLicence('ai_agent', aiAgent, setAiAgent, -1),
                          onIncrement: () => stepLicence('ai_agent', aiAgent, setAiAgent, 1),
                          disabledMinus: (parseInt(String(aiAgent), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'ai_agent',
                            parseInt(String(aiAgent), 10) || 0,
                          ),
                          error: errors.ai_agent,
                        },
                        {
                          key: 'ai_agent_live_channel',
                          icon: FiShare2,
                          iconModifier: 'ai',
                          title: 'AI Agent Live Channels',
                          description: 'Concurrent AI agent call channels',
                          value: parseInt(String(aiAgentLiveChannel), 10) || 0,
                          onChange: (val) =>
                            setAiAgentLiveChannel(validateInput(String(val), 'ai_agent_live_channel')),
                          onDecrement: () =>
                            stepLicence(
                              'ai_agent_live_channel',
                              aiAgentLiveChannel,
                              setAiAgentLiveChannel,
                              -1,
                            ),
                          onIncrement: () =>
                            stepLicence(
                              'ai_agent_live_channel',
                              aiAgentLiveChannel,
                              setAiAgentLiveChannel,
                              1,
                            ),
                          disabledMinus: (parseInt(String(aiAgentLiveChannel), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'ai_agent_live_channel',
                            parseInt(String(aiAgentLiveChannel), 10) || 0,
                          ),
                          error: errors.ai_agent_live_channel,
                        },
                        {
                          key: 'cps',
                          icon: FiPhone,
                          iconModifier: 'channel',
                          title: 'CPS',
                          description: 'Calls per second',
                          value: parseInt(String(cps), 10) || 0,
                          onChange: (val) => setCps(validateInput(String(val), 'cps')),
                          onDecrement: () => stepLicence('cps', cps, setCps, -1),
                          onIncrement: () => stepLicence('cps', cps, setCps, 1),
                          disabledMinus: (parseInt(String(cps), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence('cps', parseInt(String(cps), 10) || 0),
                          error: errors.cps,
                        },
                        {
                          key: 'mobile',
                          icon: FiSmartphone,
                          iconModifier: 'mobile',
                          title: 'Mobile License',
                          description: 'Mobile Device licences',
                          value: parseInt(String(mobile), 10) || 0,
                          onChange: (val) => setMobile(validateInput(String(val), 'mobile')),
                          onDecrement: () => stepLicence('mobile', mobile, setMobile, -1),
                          onIncrement: () => stepLicence('mobile', mobile, setMobile, 1),
                          disabledMinus: (parseInt(String(mobile), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence('mobile', parseInt(String(mobile), 10) || 0),
                          error: errors.mobile,
                        },
                        {
                          key: 'demo_live_agents',
                          icon: FiActivity,
                          iconModifier: 'live',
                          title: 'Demo Live Agents (Reseller)',
                          description: 'Demo live-agent pool for this reseller tree',
                          value: parseInt(String(demoLiveAgents), 10) || 0,
                          hidden: !(loginUserRole === USER_ROLES.SUPERADMIN
                            ? Boolean(isResellerCompany)
                            : Boolean(isResellerCompany) || isDemoCompany),
                          onChange: (val) =>
                            setDemoLiveAgents(validateInput(String(val), 'demo_live_agents')),
                          onDecrement: () =>
                            stepLicence('demo_live_agents', demoLiveAgents, setDemoLiveAgents, -1),
                          onIncrement: () =>
                            stepLicence('demo_live_agents', demoLiveAgents, setDemoLiveAgents, 1),
                          disabledMinus: (parseInt(String(demoLiveAgents), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'demo_live_agents',
                            parseInt(String(demoLiveAgents), 10) || 0,
                          ),
                          error: errors.demo_live_agents,
                        },
                        {
                          key: 'company',
                          icon: FaBuilding,
                          iconModifier: 'company',
                          title: 'Company',
                          description: 'Additional companies',
                          value: parseInt(String(asCompany), 10) || 0,
                          hidden: isOwnCompany,
                          onChange: (val) => setAsCompany(validateInput(String(val), 'company')),
                          onDecrement: () => stepLicence('company', asCompany, setAsCompany, -1),
                          onIncrement: () => stepLicence('company', asCompany, setAsCompany, 1),
                          disabledMinus: (parseInt(String(asCompany), 10) || 0) <= 0,
                          disabledPlus: !canIncrementLicence(
                            'company',
                            parseInt(String(asCompany), 10) || 0,
                          ),
                          error: errors.company,
                        },
                      ]}
                      availableSummary={
                        loginUserRole !== USER_ROLES.SUPERADMIN ? (
                          <div className="licence-available-summary">
                            <h6 className="licence-available-summary__title">
                              Available Licence (Allocated Company)
                            </h6>
                            <div className="licence-available-summary__row">
                              <span>
                                Call Centers: {parentLicence?.available_licence?.role_ccadmin ?? 0}
                              </span>
                              <span>
                                Agent: {parentLicence?.available_licence?.role_ccagent ?? 0}
                              </span>
                              <span>
                                Total Team Lead:{' '}
                                {parentLicence?.available_licence?.role_teamlead ?? 0}
                              </span>
                              <span>
                                Live Agent: {parentLicence?.available_licence?.live_agent ?? 0}
                              </span>
                              <span>
                                Channels: {parentLicence?.available_licence?.channel ?? 0}
                              </span>
                              <span>
                                AI Agents: {parentLicence?.available_licence?.ai_agent ?? 0}
                              </span>
                              <span>
                                AI Live Channels:{' '}
                                {parentLicence?.available_licence?.ai_agent_live_channel ?? 0}
                              </span>
                              <span>CPS: {parentLicence?.available_licence?.cps ?? 0}</span>
                              <span>Mobile: {parentLicence?.available_licence?.mobile ?? 0}</span>
                              {!isOwnCompany && (
                                <span>
                                  Company: {parentLicence?.available_licence?.company ?? 0}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : null
                      }
                    />
                  )}
                  </>
                )}

                {/* ── Step 3: Branding ───────────────────────────── */}
                {stepper.current === 3 && (
                  <div className="company-type-section-v2 mt-3">
                    <Form.Label className="ccf-field-label d-block">Panel Branding</Form.Label>
                    <p className="text-muted small mb-3">
                      Shown in the sidebar to every user assigned to this company, including its
                      call centre admins and agents. Leave blank to inherit the parent company's
                      branding.
                    </p>
                    <CompanyBrandingFields
                      domain={domain}
                      setDomain={setDomain}
                      domainError={domainError}
                      setDomainError={setDomainError}
                      panelDisplayName={panelDisplayName}
                      setPanelDisplayName={setPanelDisplayName}
                      logoPreview={logoPreview}
                      logoError={logoError}
                      onLogoChange={handleLogoChange}
                    />
                  </div>
                )}

                {/* ── Step 4: Default User + Submit ──────────────── */}
                {stepper.current === 4 && (
                  <>
                  {(isResellerCompany || isOwnCompany) && (
                    <>
                      <div className="company-type-section-v2 mt-3">
                        <Form.Label className="ccf-field-label d-block">Default User</Form.Label>
                        <div className="row g-3 align-items-end">
                          <div className="col-12 col-md-6">
                            <div
                              role="button"
                              tabIndex={0}
                              className={`company-type-card-v2 ${
                                createDefaultUser ? 'is-selected' : ''
                              }`}
                              onClick={() => setCreateDefaultUser((prev) => !prev)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setCreateDefaultUser((prev) => !prev);
                                }
                              }}
                            >
                              <span className="company-type-card-v2__radio" aria-hidden="true" />
                              <div className="company-type-card-v2__body">
                                <div className="company-type-card-v2__title">
                                  Create Default User
                                </div>
                                <div className="company-type-card-v2__desc">
                                  Optional login account you can switch to from the company list
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {createDefaultUser && (
                        <DefaultUserForm
                          isResellerCompany={isResellerCompany}
                          name={defaultUserName}
                          setName={setDefaultUserName}
                          username={defaultUsername}
                          setUsername={setDefaultUsername}
                          usernameError={defaultUsernameError}
                          nameError={defaultNameError}
                          usernameTakenMessage={defaultUsernameTakenMessage}
                          usernameStatus={defaultUsernameStatus}
                          emailStatus={defaultEmailStatus}
                          onUsernameChange={handleDefaultUsernameChange}
                          onUsernameBlur={handleDefaultUsernameBlur}
                          onNameChange={handleDefaultNameChange}
                          onNameBlur={handleDefaultNameBlur}
                          email={defaultUserEmail}
                          onEmailChange={handleDefaultUserEmailChange}
                          expiryDate={defaultUserExpiryDate}
                          setExpiryDate={(val) => {
                            setDefaultUserExpiryDate(val);
                            if (formErrors.defaultUserExpiry) {
                              setFormErrors((prev) => ({ ...prev, defaultUserExpiry: '' }));
                            }
                          }}
                          minExpiryDate={todayInputString()}
                          maxExpiryDate={defaultUserMaxExpiry || parentExpiryDate}
                          password={defaultUserPassword}
                          setPassword={(val) => {
                            setDefaultUserPassword(val);
                            if (formErrors.defaultUserPassword) {
                              setFormErrors((prev) => ({ ...prev, defaultUserPassword: '' }));
                            }
                          }}
                          confirmPassword={defaultUserConfirmPassword}
                          setConfirmPassword={(val) => {
                            setDefaultUserConfirmPassword(val);
                            if (formErrors.defaultUserConfirmPassword) {
                              setFormErrors((prev) => ({
                                ...prev,
                                defaultUserConfirmPassword: '',
                              }));
                            }
                          }}
                          showPassword={showDefaultPassword}
                          setShowPassword={setShowDefaultPassword}
                          aclValue={defaultUserAcl}
                          setAclValue={(val) => {
                            setDefaultUserAcl(val);
                            if (formErrors.defaultUserAcl) {
                              setFormErrors((prev) => ({ ...prev, defaultUserAcl: '' }));
                            }
                          }}
                          aclList={defaultUserAclList}
                          roleValue={defaultUserRoleSelection}
                          setRoleValue={(val) => {
                            setDefaultUserRoleSelection(val);
                            if (formErrors.defaultUserRole) {
                              setFormErrors((prev) => ({ ...prev, defaultUserRole: '' }));
                            }
                          }}
                          roleOptions={defaultUserRoleSelectOptions}
                          signupEmailOtp={signupEmailOtp}
                          setSignupEmailOtp={setSignupEmailOtp}
                          emailVerified={emailVerifiedForCreate}
                          signupEmailBusy={signupEmailBusy}
                          onSendCode={sendDefaultUserSignupEmailCode}
                          onVerifyOtp={verifyDefaultUserSignupEmailOtp}
                          emailVerificationRequired={defaultUserEmailVerificationRequired}
                          showAccountType={isCreatorSuperAdmin}
                          accountType={defaultUserAccountType}
                          setAccountType={(val) => {
                            setDefaultUserAccountType(val);
                            if (formErrors.defaultUserAccountType) {
                              setFormErrors((prev) => ({ ...prev, defaultUserAccountType: '' }));
                            }
                          }}
                        />
                      )}
                    </>
                  )}

                  <div className="form-actions">
                    <Button
                      onClick={submitForm}
                      type="button"
                      variant="btn btn-primary"
                      disabled={!canSubmitForm || isSubmitting}
                    >
                      <FaBuilding className="me-2" />{' '}
                      {createDefaultUser ? 'Create Company & Default User' : 'Create Company'}{' '}
                      &nbsp;
                      {isSubmitting ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      ) : (
                        <></>
                      )}
                    </Button>
                  </div>
                  </>
                )}

                {/* Wizard nav — Prev on every step > 0; Next until the
                    final step (which has its own Submit above). */}
                <div className="mt-6 flex items-center justify-between">
                  <ShadPageButton
                    type="button"
                    variant="ghost"
                    onClick={stepper.prev}
                    disabled={stepper.current === 0}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </ShadPageButton>
                  {stepper.current < CREATE_COMPANY_STEPS.length - 1 && (
                    <ShadPageButton type="button" onClick={handleWizardNext}>
                      Next
                      <ArrowRight className="h-4 w-4" />
                    </ShadPageButton>
                  )}
                </div>
                </form>
              </div>
            </div>
          </ShadcnCard>
        </Col>
      </Row>
    </FillPage>
  );
};

export default CompanyCreate;
